// UI.jsx – shared components, plain CSS only (no Tailwind)
import React, { useEffect } from 'react';

/* ── Toast ─────────────────────────────────────────────────── */
export function Toast({ message, type = 'success', onClose }) {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onClose, 3800);
    return () => clearTimeout(t);
  }, [message, onClose]);
  if (!message) return null;
  return (
    <div className={`toast ${type}`} role="alert">
      <span className="toast-msg">{message}</span>
      <button className="toast-close" onClick={onClose} aria-label="Close">✕</button>
    </div>
  );
}

/* ── StatusBadge ────────────────────────────────────────────── */
export function StatusBadge({ status }) {
  const cls = `badge badge-${(status || 'draft').replace(/[^a-z_]/gi, '')}`;
  return <span className={cls}>{(status || '—').replace(/_/g, ' ')}</span>;
}

/* ── StatCard ───────────────────────────────────────────────── */
export function StatCard({ label, value, sub, color = 'red' }) {
  return (
    <div className={`stat-card ${color}`}>
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value ?? '—'}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

/* ── Modal ──────────────────────────────────────────────────── */
export function Modal({ open, title, onClose, children, footer, wide }) {
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);
  if (!open) return null;
  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={`modal-box${wide ? ' wide' : ''}`} role="dialog">
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

/* ── Field ──────────────────────────────────────────────────── */
export function Field({ label, required, children }) {
  return (
    <div className="field">
      <label className="field-label">
        {label}{required && <span className="req">*</span>}
      </label>
      {children}
    </div>
  );
}

/* ── Input ──────────────────────────────────────────────────── */
export function Input({ readOnly, className = '', ...props }) {
  return (
    <input
      {...props}
      readOnly={readOnly}
      className={`inp${readOnly ? ' inp-readonly' : ''} ${className}`}
    />
  );
}

/* ── Select ─────────────────────────────────────────────────── */
export function Select({ children, className = '', ...props }) {
  return (
    <select {...props} className={`sel ${className}`}>
      {children}
    </select>
  );
}

/* ── Btn ────────────────────────────────────────────────────── */
export function Btn({ variant = 'primary', size = '', className = '', children, ...props }) {
  return (
    <button
      {...props}
      className={`btn btn-${variant}${size ? ` btn-${size}` : ''} ${className}`}
    >
      {children}
    </button>
  );
}

/* ── Spinner ────────────────────────────────────────────────── */
export function Spinner({ text = 'Loading…' }) {
  return (
    <div className="spinner-wrap">
      <div className="spinner" />
      <span className="spinner-text">{text}</span>
    </div>
  );
}

/* ── PageHeader ─────────────────────────────────────────────── */
export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="page-header">
      <div className="page-header-left">
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {actions && <div className="page-header-actions">{actions}</div>}
    </div>
  );
}

/* ── CardTitle ──────────────────────────────────────────────── */
export function CardTitle({ children, right }) {
  return (
    <div className="card-header">
      <span>{children}</span>
      {right && <span>{right}</span>}
    </div>
  );
}

/* ── Table ──────────────────────────────────────────────────── */
export function Table({ cols, rows, empty, loading }) {
  return (
    <div className="tbl-wrap">
      <table className="tbl">
        <thead>
          <tr>
            {cols.map(c => <th key={c.label || c}>{c.label || c}</th>)}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={cols.length} className="tbl-empty">Loading…</td></tr>
          ) : rows && rows.length > 0 ? rows : (
            <tr><td colSpan={cols.length} className="tbl-empty">{empty || 'No data found.'}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
